import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  ComponentCategory, 
  SelectedComponents, 
  ComponentSelection,
  GlobalSettings,
  CATEGORY_INFO,
  ValidationResult,
  MenuItemTextStyle,
  ButtonStyle,
  DEFAULT_MENU_ITEM_TEXT_REST,
  DEFAULT_MENU_ITEM_TEXT_HOVER,
  DEFAULT_BUTTON_REST_STATE,
  DEFAULT_BUTTON_HOVER_STATE,
  DEFAULT_BUTTON_SHADOW_REST,
  DEFAULT_BUTTON_SHADOW_HOVER,
  SearchButtonStyle
} from '@/types/codeInjection';
import { componentsByCategory } from '@/constants/codeInjectionTemplates';
import { 
  validateSelectedComponentsEnhanced, 
  getComponentConflicts,
  isValidSelectionForCategory 
} from '@/utils/codeInjectionGenerator';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import OverlayStrengthSelector from './OverlayStrengthSelector';
import MovementStrengthSelector from './MovementStrengthSelector';
import GlowStrengthSelector from './GlowStrengthSelector';
import DescriptionFadeStrengthSelector from './DescriptionFadeStrengthSelector';
import MenuItemTextEditor from './MenuItemTextEditor';
import CategoryHeaderTextColorSelector from './CategoryHeaderTextColorSelector';
import CategoryHeaderTypographySelector from './CategoryHeaderTypographySelector';
import CategoryHeaderGradientSelector from './CategoryHeaderGradientSelector';
import NavLogoGradientSelector from './NavLogoGradientSelector';
import NavLogoGlowColorSelector from './NavLogoGlowColorSelector';
import CategoryUnderlineColorSelector from './CategoryUnderlineColorSelector';
import NestedComponentSelector from './NestedComponentSelector';
import ButtonStyleEditor from './ButtonStyleEditor';
import NavigationStyleEditor from './NavigationStyleEditor';
import SearchButtonStyleEditor from './SearchButtonStyleEditor';
import PriceTagStyleEditor from './PriceTagStyleEditor';
import PriceTagGradientEditor from './PriceTagGradientEditor';
import WelcomeMessageEditor from './WelcomeMessageEditor';
import TagBadgeStyleEditor from './TagBadgeStyleEditor';

interface ComponentSelectorProps {
  selectedComponents: SelectedComponents;
  globalSettings: GlobalSettings;
  onComponentChange: (category: ComponentCategory, componentId: string | string[] | null) => void;
  onGlobalSettingChange: (key: keyof GlobalSettings, value: any) => void;
}

/**
 * ComponentSelector - UI for selecting component variations with enhanced grouping and hybrid selection support
 */
const ComponentSelector: React.FC<ComponentSelectorProps> = ({ 
  selectedComponents, 
  globalSettings,
  onComponentChange,
  onGlobalSettingChange 
}) => {
  // Group categories by their grouping property
  const groupedCategories = React.useMemo(() => {
    const groups: Record<string, typeof CATEGORY_INFO[ComponentCategory][]> = {};
    
    Object.values(CATEGORY_INFO)
      .filter(categoryInfo => !['menuItemAnimations', 'categoryHeaders', 'buttonEffects', 'loadingAnimations', 'effectsEnhancements', 'animationsCombination'].includes(categoryInfo.id)) // Filter out legacy categories
      .sort((a, b) => a.priority - b.priority)
      .forEach(categoryInfo => {
        const group = categoryInfo.grouping || 'other';
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(categoryInfo);
      });
    
    return groups;
  }, []);

  const groupNames = {
    menuItem: 'Menu Item Effects',
    categoryHeader: 'Category Headers',
    button: 'Button Effects',
    loading: 'Page Loading',
    other: 'Other Elements'
  };

  // Helper functions for nested components
  const getNestedSelection = (category: ComponentCategory, componentId: string): { isSelected: boolean; subTemplate?: string } => {
    const currentSelection = selectedComponents[category];
    if (!Array.isArray(currentSelection)) return { isSelected: false };
    
    // Check for string-based selection (backward compatibility)
    if (currentSelection.includes(componentId)) {
      return { isSelected: true };
    }
    
    // Check for object-based selection with sub-templates
    const objectSelection = currentSelection.find((item): item is ComponentSelection => 
      typeof item === 'object' && item.id === componentId
    );
    
    return objectSelection 
      ? { isSelected: true, subTemplate: objectSelection.subTemplate }
      : { isSelected: false };
  };

  const handleNestedSelectionChange = (category: ComponentCategory, componentId: string, selected: boolean, subTemplate?: string) => {
    const currentSelection = selectedComponents[category] as (string | ComponentSelection)[] || [];
    let newSelection: (string | ComponentSelection)[];

    if (selected) {
      // Remove any existing selection for this component
      const filteredSelection = currentSelection.filter((item) => 
        typeof item === 'string' ? item !== componentId : item.id !== componentId
      );
      
      // Add new selection with sub-template if applicable
      const newItem: ComponentSelection = subTemplate 
        ? { id: componentId, subTemplate }
        : { id: componentId };
      
      newSelection = [...filteredSelection, newItem];
    } else {
      // Remove the component
      newSelection = currentSelection.filter((item) => 
        typeof item === 'string' ? item !== componentId : item.id !== componentId
      );
    }

    onComponentChange(category, newSelection);
  };

  // Get validation results
  const validationResult: ValidationResult = validateSelectedComponentsEnhanced(selectedComponents);

  // Count selected components
  const getSelectedCount = () => {
    return Object.values(selectedComponents).reduce((count, selection) => {
      if (Array.isArray(selection)) {
        return count + selection.length;
      }
      return selection ? count + 1 : count;
    }, 0);
  };

  // Handle single selection change
  const handleSingleSelectionChange = (category: ComponentCategory, value: string) => {
    onComponentChange(category, value || null);
  };

  // Handle multiple selection change
  const handleMultipleSelectionChange = (category: ComponentCategory, componentId: string, checked: boolean) => {
    const currentSelection = selectedComponents[category];
    const currentArray = Array.isArray(currentSelection) ? currentSelection : (currentSelection ? [currentSelection] : []);
    
    let newSelection: string[];
    if (checked) {
      // Special handling for category header text effects - make solid color and gradient color mutually exclusive
      if (category === 'categoryHeaderTextEffects') {
        if (componentId === 'category-text-color') {
          // Remove gradient color if selecting solid color
          newSelection = currentArray.filter(id => id !== 'category-gradient-text');
          newSelection.push(componentId);
        } else if (componentId === 'category-gradient-text') {
          // Remove solid color if selecting gradient color
          newSelection = currentArray.filter(id => id !== 'category-text-color');
          newSelection.push(componentId);
        } else {
          // For other options (like base styling), just add normally
          newSelection = [...currentArray, componentId];
        }
      } else if (category === 'menuItemTextEffects') {
        if (componentId === 'menu-item-title-color') {
          // Remove static title style if selecting dynamic
          newSelection = currentArray.filter(id => id !== 'menu-item-title-style');
          newSelection.push(componentId);
        } else if (componentId === 'menu-item-title-style') {
          // Remove dynamic title color if selecting static
          newSelection = currentArray.filter(id => id !== 'menu-item-title-color');
          newSelection.push(componentId);
        } else {
          // For other options (like description fade), just add normally
          newSelection = [...currentArray, componentId];
        }
      } else {
        newSelection = [...currentArray, componentId];
      }
    } else {
      newSelection = currentArray.filter(id => id !== componentId);
    }

    onComponentChange(category, newSelection.length > 0 ? newSelection : null);
  };

  // Check if component has conflicts
  const hasConflicts = (componentId: string) => {
    const conflicts = getComponentConflicts(componentId, selectedComponents);
    return conflicts.length > 0;
  };

  // Get selection status for a category
  const getSelectionStatus = (category: ComponentCategory) => {
    const selection = selectedComponents[category];
    if (!selection) return { count: 0, hasSelection: false };
    
    if (Array.isArray(selection)) {
      return { count: selection.length, hasSelection: selection.length > 0 };
    }
    return { count: 1, hasSelection: true };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Component Options</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {getSelectedCount()} selected
          </Badge>
          {!validationResult.isValid && (
            <Badge variant="destructive">
              {validationResult.errors.length} errors
            </Badge>
          )}
        </div>
      </div>

      {/* Validation Messages */}
      {(!validationResult.isValid || validationResult.warnings.length > 0) && (
        <div className="space-y-2">
          {validationResult.errors.map((error, index) => (
            <Alert key={index} variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ))}
          {validationResult.warnings.map((warning, index) => (
            <Alert key={index}>
              <Info className="h-4 w-4" />
              <AlertDescription>{warning}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}
      
      <div className="space-y-6">
        {Object.entries(groupedCategories).map(([groupKey, categories]) => (
          <div key={groupKey} className="space-y-3">
            {/* Group Header */}
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-900">{groupNames[groupKey as keyof typeof groupNames] || groupKey}</h4>
              <Separator className="flex-1" />
            </div>
            
            {/* Categories in this group */}
            <Accordion type="multiple" className="space-y-2">
              {categories.map((categoryInfo) => {
                const category = categoryInfo.id;
                const templates = componentsByCategory[category];
                const allowMultiple = categoryInfo.selectionType === 'multiple';
                const selectionStatus = getSelectionStatus(category);
                
                return (
                  <AccordionItem key={category} value={category}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        <span className="text-lg">{categoryInfo.icon}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{categoryInfo.name}</span>
                            {allowMultiple && (
                              <Badge variant="outline" className="text-xs">
                                Multi-Select
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{categoryInfo.description}</div>
                        </div>
                        {selectionStatus.hasSelection && (
                          <Badge variant="secondary" className="ml-auto">
                            {selectionStatus.count} selected
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    
                    <AccordionContent>
                      <Card>
                        <CardHeader className="pb-4">
                          <CardTitle className="text-base flex items-center gap-2">
                            Choose {categoryInfo.name}
                            {allowMultiple && (
                              <span className="text-sm font-normal text-gray-500">
                                (Multiple selections allowed)
                              </span>
                            )}
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent className="space-y-4">
                          {allowMultiple ? (
                            // Multiple selection with checkboxes
                            <div className="space-y-3">
                              {templates.map((template) => {
                                const conflicts = hasConflicts(template.id);
                                
                                // Use NestedComponentSelector for components with subTemplates
                                if (template.isGrouped && template.subTemplates && template.subTemplates.length > 0) {
                                  const selection = getNestedSelection(category, template.id);
                                  
                                  return (
                                    <NestedComponentSelector
                                      key={template.id}
                                      template={template}
                                      isSelected={selection.isSelected}
                                      selectedSubTemplate={selection.subTemplate}
                                      onSelectionChange={(selected, subTemplate) => 
                                        handleNestedSelectionChange(category, template.id, selected, subTemplate)
                                      }
                                      disabled={conflicts}
                                    />
                                  );
                                }
                                
                                // Regular checkbox for simple components
                                const currentSelection = selectedComponents[category];
                                const isSelected = Array.isArray(currentSelection) 
                                  ? currentSelection.some(item => 
                                      typeof item === 'string' ? item === template.id : item.id === template.id
                                    )
                                  : currentSelection === template.id;
                                
                                return (
                                  <div key={template.id} className="flex items-start space-x-3">
                                    <Checkbox
                                      id={`${category}-${template.id}`}
                                      checked={isSelected}
                                      disabled={conflicts}
                                      onCheckedChange={(checked) => 
                                        handleMultipleSelectionChange(category, template.id, checked as boolean)
                                      }
                                      className="mt-3"
                                    />
                                    <Label 
                                      htmlFor={`${category}-${template.id}`} 
                                      className="flex-1 cursor-pointer"
                                    >
                                      <div className={`p-3 rounded-md border transition-colors ${
                                        isSelected 
                                          ? 'bg-primary/5 border-primary/20' 
                                          : conflicts
                                          ? 'bg-red-50 border-red-200 opacity-60'
                                          : 'hover:bg-gray-50 border-gray-200'
                                      }`}>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-sm">{template.name}</span>
                                          {template.stackable && (
                                            <Badge variant="outline" className="text-xs">
                                              Stackable
                                            </Badge>
                                          )}
                                          {conflicts && (
                                            <Badge variant="destructive" className="text-xs">
                                              Conflicts
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {template.description}
                                        </div>
                                        {template.preview && (
                                          <div className="text-xs text-blue-600 mt-1">
                                            {template.preview}
                                          </div>
                                        )}
                                        {conflicts && (
                                          <div className="text-xs text-red-600 mt-1">
                                            Conflicts with selected components
                                          </div>
                                        )}
                                      </div>
                                    </Label>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            // Single selection with radio buttons
                            <RadioGroup
                              value={(selectedComponents[category] as string) || ''}
                              onValueChange={(value) => handleSingleSelectionChange(category, value)}
                            >
                              {/* None option */}
                              <div className="flex items-center space-x-3">
                                <RadioGroupItem value="" id={`${category}-none`} />
                                <Label 
                                  htmlFor={`${category}-none`} 
                                  className="flex-1 cursor-pointer"
                                >
                                  <div className="p-3 rounded-md border hover:bg-gray-50 transition-colors">
                                    <div className="font-medium text-sm">None</div>
                                    <div className="text-xs text-gray-500">
                                      Don't apply any {categoryInfo.name.toLowerCase()}
                                    </div>
                                  </div>
                                </Label>
                              </div>
                              
                              {/* Component options */}
                              {templates.map((template) => {
                                const isSelected = selectedComponents[category] === template.id;
                                const conflicts = hasConflicts(template.id);
                                
                                return (
                                  <div key={template.id} className="flex items-center space-x-3">
                                    <RadioGroupItem 
                                      value={template.id} 
                                      id={`${category}-${template.id}`}
                                      disabled={conflicts}
                                    />
                                    <Label 
                                      htmlFor={`${category}-${template.id}`} 
                                      className="flex-1 cursor-pointer"
                                    >
                                      <div className={`p-3 rounded-md border transition-colors ${
                                        isSelected 
                                          ? 'bg-primary/5 border-primary/20' 
                                          : conflicts
                                          ? 'bg-red-50 border-red-200 opacity-60'
                                          : 'hover:bg-gray-50 border-gray-200'
                                      }`}>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-sm">{template.name}</span>
                                          {conflicts && (
                                            <Badge variant="destructive" className="text-xs">
                                              Conflicts
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {template.description}
                                        </div>
                                        {template.preview && (
                                          <div className="text-xs text-blue-600 mt-1">
                                            {template.preview}
                                          </div>
                                        )}
                                        {conflicts && (
                                          <div className="text-xs text-red-600 mt-1">
                                            Conflicts with selected components
                                          </div>
                                        )}
                                      </div>
                                    </Label>
                                  </div>
                                );
                              })}
                            </RadioGroup>
                          )}
                          
                          {/* Add overlay strength selector for image effects */}
                          {category === 'menuItemImageEffects' && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <OverlayStrengthSelector
                                value={globalSettings.overlayStrength || 0.3}
                                onChange={(strength) => onGlobalSettingChange('overlayStrength', strength)}
                              />
                            </div>
                          )}
                          
                          {/* Add movement strength selector for movement effects */}
                          {category === 'menuItemMovement' && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <MovementStrengthSelector
                                value={globalSettings.movementStrength || 1.0}
                                onChange={(strength) => onGlobalSettingChange('movementStrength', strength)}
                              />
                            </div>
                          )}
                          
                          {/* Add glow strength selector for glow effects */}
                          {category === 'menuItemGlow' && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <GlowStrengthSelector
                                value={globalSettings.glowStrength || 1.0}
                                onChange={(strength) => onGlobalSettingChange('glowStrength', strength)}
                              />
                            </div>
                          )}
                          
                          {/* Add comprehensive text editor for menu item text effects */}
                          {category === 'menuItemTextEffects' && (
                            <div className="mt-6 pt-4 border-t border-gray-200 space-y-6">
                              {(() => {
                                const textEffectsSelection = selectedComponents.menuItemTextEffects;
                                const hasStaticEnhancements = Array.isArray(textEffectsSelection) && 
                                  textEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'menu-item-title-style' : item.id === 'menu-item-title-style'
                                  );
                                const hasDescriptionFade = Array.isArray(textEffectsSelection) && 
                                  textEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'menu-item-description-fade' : item.id === 'menu-item-description-fade'
                                  );
                                
                                return (
                                  <>
                                    {/* Show description fade controls when selected */}
                                    {hasDescriptionFade && (
                                      <DescriptionFadeStrengthSelector
                                        value={globalSettings.descriptionFadeStrength || 1.0}
                                        onChange={(strength) => onGlobalSettingChange('descriptionFadeStrength', strength)}
                                      />
                                    )}
                                    
                                    {/* Show text editor when either static or dynamic enhancements are selected */}
                                    {(hasStaticEnhancements || textEffectsSelection?.some(item => 
                                      typeof item === 'string' ? item === 'menu-item-title-color' : item.id === 'menu-item-title-color'
                                    )) && (
                                      <MenuItemTextEditor
                                        restState={globalSettings.menuItemTextRest || DEFAULT_MENU_ITEM_TEXT_REST}
                                        hoverState={globalSettings.menuItemTextHover || DEFAULT_MENU_ITEM_TEXT_HOVER}
                                        onRestStateChange={(style) => onGlobalSettingChange('menuItemTextRest', style)}
                                        onHoverStateChange={(style) => onGlobalSettingChange('menuItemTextHover', style)}
                                        primaryColor={globalSettings.primaryColor}
                                        secondaryColor={globalSettings.secondaryColor}
                                        isStaticMode={hasStaticEnhancements}
                                      />
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          
                          {/* Add controls for category header text effects */}
                          {category === 'categoryHeaderTextEffects' && (
                            <div className="mt-6 pt-4 border-t border-gray-200 space-y-6">
                              {/* Show color picker only when Solid Color is selected */}
                              {(() => {
                                const textEffectsSelection = selectedComponents.categoryHeaderTextEffects;
                                const hasSolidColor = Array.isArray(textEffectsSelection) && 
                                  textEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'category-text-color' : item.id === 'category-text-color'
                                  );
                                
                                if (hasSolidColor) {
                                  return (
                                    <CategoryHeaderTextColorSelector
                                      value={globalSettings.categoryHeaderTextColor || '#f0f0f0'}
                                      primaryColor={globalSettings.primaryColor}
                                      secondaryColor={globalSettings.secondaryColor}
                                      onChange={(color) => onGlobalSettingChange('categoryHeaderTextColor', color)}
                                    />
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Show gradient color picker only when Gradient Color is selected */}
                              {(() => {
                                const textEffectsSelection = selectedComponents.categoryHeaderTextEffects;
                                const hasGradientColor = Array.isArray(textEffectsSelection) && 
                                  textEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'category-gradient-text' : item.id === 'category-gradient-text'
                                  );
                                
                                if (hasGradientColor) {
                                  return (
                                    <CategoryHeaderGradientSelector
                                      primaryColor={globalSettings.categoryHeaderGradientPrimary || globalSettings.primaryColor}
                                      secondaryColor={globalSettings.categoryHeaderGradientSecondary || globalSettings.secondaryColor}
                                      globalPrimaryColor={globalSettings.primaryColor}
                                      globalSecondaryColor={globalSettings.secondaryColor}
                                      onPrimaryColorChange={(color) => onGlobalSettingChange('categoryHeaderGradientPrimary', color)}
                                      onSecondaryColorChange={(color) => onGlobalSettingChange('categoryHeaderGradientSecondary', color)}
                                    />
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Show typography controls only when Base Styling is selected */}
                              {(() => {
                                const textEffectsSelection = selectedComponents.categoryHeaderTextEffects;
                                const hasBaseStyling = Array.isArray(textEffectsSelection) && 
                                  textEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'category-base-styling' : item.id === 'category-base-styling'
                                  );
                                
                                if (hasBaseStyling) {
                                  return (
                                    <CategoryHeaderTypographySelector
                                      fontWeight={globalSettings.fontWeight || 700}
                                      fontSize={globalSettings.fontSize || 28}
                                      fontStyle={globalSettings.fontStyle || 'normal'}
                                      onFontWeightChange={(weight) => onGlobalSettingChange('fontWeight', weight)}
                                      onFontSizeChange={(size) => onGlobalSettingChange('fontSize', size)}
                                      onFontStyleChange={(style) => onGlobalSettingChange('fontStyle', style)}
                                    />
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          
                          {/* Add controls for nav bar text effects */}
                          {category === 'navBarTextEffects' && (
                            <div className="mt-6 pt-4 border-t border-gray-200 space-y-6">
                              {/* Show gradient color picker only when Logo Text Gradient is selected */}
                              {(() => {
                                const navTextEffectsSelection = selectedComponents.navBarTextEffects;
                                const hasLogoGradient = Array.isArray(navTextEffectsSelection) && 
                                  navTextEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'nav-logo-gradient' : item.id === 'nav-logo-gradient'
                                  );
                                
                                if (hasLogoGradient) {
                                  return (
                                    <NavLogoGradientSelector
                                      primaryColor={globalSettings.navLogoGradientPrimary || globalSettings.primaryColor}
                                      secondaryColor={globalSettings.navLogoGradientSecondary || globalSettings.secondaryColor}
                                      globalPrimaryColor={globalSettings.primaryColor}
                                      globalSecondaryColor={globalSettings.secondaryColor}
                                      onPrimaryColorChange={(color) => onGlobalSettingChange('navLogoGradientPrimary', color)}
                                      onSecondaryColorChange={(color) => onGlobalSettingChange('navLogoGradientSecondary', color)}
                                    />
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Show glow color picker only when Logo Text Glow Effect is selected */}
                              {(() => {
                                const navTextEffectsSelection = selectedComponents.navBarTextEffects;
                                const hasLogoGlow = Array.isArray(navTextEffectsSelection) && 
                                  navTextEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'nav-logo-text-glow' : item.id === 'nav-logo-text-glow'
                                  );
                                
                                if (hasLogoGlow) {
                                  return (
                                    <NavLogoGlowColorSelector
                                      navLogoGlowColor={globalSettings.navLogoGlowColor}
                                      globalPrimaryColor={globalSettings.primaryColor}
                                      globalSecondaryColor={globalSettings.secondaryColor}
                                      onNavLogoGlowColorChange={(color) => onGlobalSettingChange('navLogoGlowColor', color)}
                                    />
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          
                          {/* Add controls for search button effects */}
                          {category === 'searchButtonEffects' && (
                            <div className="mt-6 pt-4 border-t border-gray-200 space-y-6">
                              {/* Show search button style editor only when Enhanced Search Button is selected */}
                              {(() => {
                                const searchButtonEffectsSelection = selectedComponents.searchButtonEffects;
                                const hasEnhancedSearchButton = Array.isArray(searchButtonEffectsSelection) && 
                                  searchButtonEffectsSelection.some(item => 
                                    typeof item === 'string' ? item === 'search-button-enhanced' : item.id === 'search-button-enhanced'
                                  );
                                
                                if (hasEnhancedSearchButton) {
                                  return (
                                    <SearchButtonStyleEditor
                                      restState={globalSettings.searchButtonRestState || {
                                        backgroundColor: globalSettings.primaryColor,
                                        svgColor: '#ffffff'
                                      }}
                                      hoverState={globalSettings.searchButtonHoverState || {
                                        backgroundColor: globalSettings.secondaryColor,
                                        svgColor: '#ffffff'
                                      }}
                                      onRestStateChange={(style) => onGlobalSettingChange('searchButtonRestState', style)}
                                      onHoverStateChange={(style) => onGlobalSettingChange('searchButtonHoverState', style)}
                                      primaryColor={globalSettings.primaryColor}
                                      secondaryColor={globalSettings.secondaryColor}
                                    />
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          
                          {/* Add comprehensive color picker for category underline styles */}
                          {category === 'categoryHeaderStyle' && (() => {
                            const selectedTemplate = selectedComponents.categoryHeaderStyle as string || null;
                            const hasUnderlineTemplate = selectedTemplate && selectedTemplate.includes('underline');
                            
                            if (hasUnderlineTemplate) {
                              return (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <CategoryUnderlineColorSelector
                                    selectedTemplate={selectedTemplate}
                                    categoryUnderlineColor={globalSettings.categoryUnderlineColor}
                                    categoryUnderlinePrimaryColor={globalSettings.categoryUnderlinePrimaryColor}
                                    categoryUnderlineSecondaryColor={globalSettings.categoryUnderlineSecondaryColor}
                                    globalPrimaryColor={globalSettings.primaryColor}
                                    globalSecondaryColor={globalSettings.secondaryColor}
                                    onCategoryUnderlineColorChange={(color) => onGlobalSettingChange('categoryUnderlineColor', color)}
                                    onCategoryUnderlinePrimaryColorChange={(color) => onGlobalSettingChange('categoryUnderlinePrimaryColor', color)}
                                    onCategoryUnderlineSecondaryColorChange={(color) => onGlobalSettingChange('categoryUnderlineSecondaryColor', color)}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Add controls for button hover effects */}
                          {category === 'buttonHoverEffect' && (() => {
                            const selectedButtonEffect = selectedComponents.buttonHoverEffect as string || null;
                            const hasButtonEffect = selectedButtonEffect && 
                              ['button-hover-scale', 'button-hover-lift', 'button-hover-lift-and-color', 'button-hover-scale-and-color'].includes(selectedButtonEffect);
                            
                            if (hasButtonEffect) {
                              // Show color controls only for the "and-color" variants
                              const showColorControls = selectedButtonEffect === 'button-hover-lift-and-color' || 
                                                       selectedButtonEffect === 'button-hover-scale-and-color';
                              
                              // Create dynamic defaults using theme colors
                              const dynamicRestDefault = globalSettings.buttonRestState || {
                                ...DEFAULT_BUTTON_REST_STATE,
                                backgroundColor: globalSettings.primaryColor
                              };
                              
                              const dynamicHoverDefault = globalSettings.buttonHoverState || {
                                ...DEFAULT_BUTTON_HOVER_STATE,
                                backgroundColor: globalSettings.secondaryColor
                              };
                              
                              return (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <ButtonStyleEditor
                                    restState={dynamicRestDefault}
                                    hoverState={dynamicHoverDefault}
                                    restShadow={globalSettings.buttonShadowRest || DEFAULT_BUTTON_SHADOW_REST}
                                    hoverShadow={globalSettings.buttonShadowHover || DEFAULT_BUTTON_SHADOW_HOVER}
                                    onRestStateChange={(style) => onGlobalSettingChange('buttonRestState', style)}
                                    onHoverStateChange={(style) => onGlobalSettingChange('buttonHoverState', style)}
                                    onRestShadowChange={(shadow) => onGlobalSettingChange('buttonShadowRest', shadow)}
                                    onHoverShadowChange={(shadow) => onGlobalSettingChange('buttonShadowHover', shadow)}
                                    primaryColor={globalSettings.primaryColor}
                                    secondaryColor={globalSettings.secondaryColor}
                                    showColorControls={showColorControls}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Add controls for navigation styles */}
                          {category === 'navigationStyles' && (() => {
                            const selectedNavigation = selectedComponents.navigationStyles as string || null;
                            const hasNavigationStyles = selectedNavigation === 'nav-customizable';
                            
                            if (hasNavigationStyles) {
                              return (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <NavigationStyleEditor
                                    navMainBg={globalSettings.navMainBg || globalSettings.primaryColor}
                                    navMainBorder={globalSettings.navMainBorder || globalSettings.secondaryColor}
                                    navTopBg={globalSettings.navTopBg || globalSettings.primaryColor}
                                    navTopBorder={globalSettings.navTopBorder || globalSettings.secondaryColor}
                                    navTopText={globalSettings.navTopText || '#ffffff'}
                                    navBottomBg={globalSettings.navBottomBg || globalSettings.primaryColor}
                                    navBottomBorder={globalSettings.navBottomBorder || globalSettings.secondaryColor}
                                    navLogoText={globalSettings.navLogoText || '#ffffff'}
                                    navSliderBg={globalSettings.navSliderBg || globalSettings.primaryColor}
                                    navSliderText={globalSettings.navSliderText || '#ffffff'}
                                    navSearchButtonBg={globalSettings.navSearchButtonBg || globalSettings.secondaryColor}
                                    navSearchButtonText={globalSettings.navSearchButtonText || '#ffffff'}
                                    onNavMainBgChange={(color) => onGlobalSettingChange('navMainBg', color)}
                                    onNavMainBorderChange={(color) => onGlobalSettingChange('navMainBorder', color)}
                                    onNavTopBgChange={(color) => onGlobalSettingChange('navTopBg', color)}
                                    onNavTopBorderChange={(color) => onGlobalSettingChange('navTopBorder', color)}
                                    onNavTopTextChange={(color) => onGlobalSettingChange('navTopText', color)}
                                    onNavBottomBgChange={(color) => onGlobalSettingChange('navBottomBg', color)}
                                    onNavBottomBorderChange={(color) => onGlobalSettingChange('navBottomBorder', color)}
                                    onNavLogoTextChange={(color) => onGlobalSettingChange('navLogoText', color)}
                                    onNavSliderBgChange={(color) => onGlobalSettingChange('navSliderBg', color)}
                                    onNavSliderTextChange={(color) => onGlobalSettingChange('navSliderText', color)}
                                    onNavSearchButtonBgChange={(color) => onGlobalSettingChange('navSearchButtonBg', color)}
                                    onNavSearchButtonTextChange={(color) => onGlobalSettingChange('navSearchButtonText', color)}
                                    primaryColor={globalSettings.primaryColor}
                                    secondaryColor={globalSettings.secondaryColor}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Add controls for price tag enhancements */}
                          {category === 'priceTagEnhancements' && (() => {
                            const selectedPriceTag = selectedComponents.priceTagEnhancements;
                            const hasPriceTagSingleColor = selectedPriceTag === 'price-tag-single-color';
                            const hasPriceTagGradient = selectedPriceTag === 'price-tag-gradient';
                            
                            return (
                              <div className="space-y-6">
                                {hasPriceTagSingleColor && (
                                  <div className="mt-6 pt-4 border-t border-gray-200">
                                    <PriceTagStyleEditor
                                      backgroundColor={globalSettings.priceTagBackgroundColor || globalSettings.primaryColor}
                                      textColor={globalSettings.priceTagTextColor || '#ffffff'}
                                      onBackgroundColorChange={(color) => onGlobalSettingChange('priceTagBackgroundColor', color)}
                                      onTextColorChange={(color) => onGlobalSettingChange('priceTagTextColor', color)}
                                      primaryColor={globalSettings.primaryColor}
                                      secondaryColor={globalSettings.secondaryColor}
                                    />
                                  </div>
                                )}
                                
                                {hasPriceTagGradient && (
                                  <div className="mt-6 pt-4 border-t border-gray-200">
                                    <PriceTagGradientEditor
                                      gradientDirection={globalSettings.priceTagGradientDirection || '135deg'}
                                      gradientStartColor={globalSettings.priceTagGradientStartColor || globalSettings.primaryColor}
                                      gradientEndColor={globalSettings.priceTagGradientEndColor || globalSettings.secondaryColor}
                                      textColor={globalSettings.priceTagGradientTextColor || '#ffffff'}
                                      onGradientDirectionChange={(direction) => onGlobalSettingChange('priceTagGradientDirection', direction)}
                                      onGradientStartColorChange={(color) => onGlobalSettingChange('priceTagGradientStartColor', color)}
                                      onGradientEndColorChange={(color) => onGlobalSettingChange('priceTagGradientEndColor', color)}
                                      onTextColorChange={(color) => onGlobalSettingChange('priceTagGradientTextColor', color)}
                                      primaryColor={globalSettings.primaryColor}
                                      secondaryColor={globalSettings.secondaryColor}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          
                          {/* Add controls for welcome messages */}
                          {category === 'welcomeMessages' && (() => {
                            const selectedWelcomeMessage = selectedComponents.welcomeMessages;
                            const hasWelcomeMessage = Array.isArray(selectedWelcomeMessage) && 
                              selectedWelcomeMessage.some(item => 
                                typeof item === 'string' ? item === 'welcome-greeting-basic' : item.id === 'welcome-greeting-basic'
                              );
                            
                            if (hasWelcomeMessage) {
                              return (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <WelcomeMessageEditor
                                    morningText={globalSettings.welcomeMessageMorningText || 'Good morning!'}
                                    afternoonText={globalSettings.welcomeMessageAfternoonText || 'Good afternoon!'}
                                    eveningText={globalSettings.welcomeMessageEveningText || 'Good evening!'}
                                    secondaryText={globalSettings.welcomeMessageSecondaryText || 'Welcome to {restaurant name}!'}
                                    restaurantName={globalSettings.restaurantName || 'our restaurant'}
                                    morningHour={globalSettings.welcomeMessageMorningHour || 12}
                                    afternoonHour={globalSettings.welcomeMessageAfternoonHour || 18}
                                    eveningHour={globalSettings.welcomeMessageEveningHour || 24}
                                    textColor={globalSettings.welcomeMessageTextColor || '#ffffff'}
                                    gradientStartColor={globalSettings.welcomeMessageGradientStartColor || globalSettings.primaryColor}
                                    gradientEndColor={globalSettings.welcomeMessageGradientEndColor || globalSettings.secondaryColor}
                                    borderColor={globalSettings.welcomeMessageBorderColor || 'rgba(255, 255, 255, 0.1)'}
                                    onMorningTextChange={(text) => onGlobalSettingChange('welcomeMessageMorningText', text)}
                                    onAfternoonTextChange={(text) => onGlobalSettingChange('welcomeMessageAfternoonText', text)}
                                    onEveningTextChange={(text) => onGlobalSettingChange('welcomeMessageEveningText', text)}
                                    onSecondaryTextChange={(text) => onGlobalSettingChange('welcomeMessageSecondaryText', text)}
                                    onRestaurantNameChange={(name) => onGlobalSettingChange('restaurantName', name)}
                                    onMorningHourChange={(hour) => onGlobalSettingChange('welcomeMessageMorningHour', hour)}
                                    onAfternoonHourChange={(hour) => onGlobalSettingChange('welcomeMessageAfternoonHour', hour)}
                                    onEveningHourChange={(hour) => onGlobalSettingChange('welcomeMessageEveningHour', hour)}
                                    onTextColorChange={(color) => onGlobalSettingChange('welcomeMessageTextColor', color)}
                                    onGradientStartColorChange={(color) => onGlobalSettingChange('welcomeMessageGradientStartColor', color)}
                                    onGradientEndColorChange={(color) => onGlobalSettingChange('welcomeMessageGradientEndColor', color)}
                                    onBorderColorChange={(color) => onGlobalSettingChange('welcomeMessageBorderColor', color)}
                                    primaryColor={globalSettings.primaryColor}
                                    secondaryColor={globalSettings.secondaryColor}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Add controls for tag badge styles */}
                          {category === 'tagBadgeStyles' && (() => {
                            const selectedBadges = selectedComponents.tagBadgeStyles;
                            const hasEnhancedBadge = Array.isArray(selectedBadges) &&
                              selectedBadges.some(item =>
                                typeof item === 'string' ? item === 'tag-badge-enhanced' : item.id === 'tag-badge-enhanced'
                              );
                            const hasSpecialBadge = Array.isArray(selectedBadges) &&
                              selectedBadges.some(item =>
                                typeof item === 'string' ? item === 'tag-badge-special' : item.id === 'tag-badge-special'
                              );

                            if (hasEnhancedBadge || hasSpecialBadge) {
                              return (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <TagBadgeStyleEditor
                                    // Enhanced badge props
                                    enhancedPrimaryColor={globalSettings.tagBadgeEnhancedPrimaryColor || globalSettings.primaryColor}
                                    enhancedSecondaryColor={globalSettings.tagBadgeEnhancedSecondaryColor || globalSettings.secondaryColor}
                                    onEnhancedPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeEnhancedPrimaryColor', color)}
                                    onEnhancedSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeEnhancedSecondaryColor', color)}
                                    showEnhancedSection={hasEnhancedBadge}

                                    // Special badge props
                                    showSpecialSection={hasSpecialBadge}
                                    veganPrimaryColor={globalSettings.tagBadgeVeganPrimaryColor || '#26c526'}
                                    veganSecondaryColor={globalSettings.tagBadgeVeganSecondaryColor || '#166a16'}
                                    onVeganPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeVeganPrimaryColor', color)}
                                    onVeganSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeVeganSecondaryColor', color)}
                                    vegetarianPrimaryColor={globalSettings.tagBadgeVegetarianPrimaryColor || '#32CD32'}
                                    vegetarianSecondaryColor={globalSettings.tagBadgeVegetarianSecondaryColor || '#36AB36'}
                                    onVegetarianPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeVegetarianPrimaryColor', color)}
                                    onVegetarianSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeVegetarianSecondaryColor', color)}
                                    glutenFreePrimaryColor={globalSettings.tagBadgeGlutenFreePrimaryColor || '#FFB347'}
                                    glutenFreeSecondaryColor={globalSettings.tagBadgeGlutenFreeSecondaryColor || '#FF8C00'}
                                    onGlutenFreePrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeGlutenFreePrimaryColor', color)}
                                    onGlutenFreeSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeGlutenFreeSecondaryColor', color)}
                                    spicyPrimaryColor={globalSettings.tagBadgeSpicyPrimaryColor || '#FF6B6B'}
                                    spicySecondaryColor={globalSettings.tagBadgeSpicySecondaryColor || '#FF3333'}
                                    onSpicyPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeSpicyPrimaryColor', color)}
                                    onSpicySecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeSpicySecondaryColor', color)}
                                    dairyFreePrimaryColor={globalSettings.tagBadgeDairyFreePrimaryColor || '#87CEEB'}
                                    dairyFreeSecondaryColor={globalSettings.tagBadgeDairyFreeSecondaryColor || '#4682B4'}
                                    onDairyFreePrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeDairyFreePrimaryColor', color)}
                                    onDairyFreeSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeDairyFreeSecondaryColor', color)}
                                    nutFreePrimaryColor={globalSettings.tagBadgeNutFreePrimaryColor || '#DEB887'}
                                    nutFreeSecondaryColor={globalSettings.tagBadgeNutFreeSecondaryColor || '#8B7355'}
                                    onNutFreePrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeNutFreePrimaryColor', color)}
                                    onNutFreeSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeNutFreeSecondaryColor', color)}
                                    halalPrimaryColor={globalSettings.tagBadgeHalalPrimaryColor || '#019000'}
                                    halalSecondaryColor={globalSettings.tagBadgeHalalSecondaryColor || '#B8860B'}
                                    onHalalPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeHalalPrimaryColor', color)}
                                    onHalalSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeHalalSecondaryColor', color)}

                                    // Popular badge colors
                                    popularPrimaryColor={globalSettings.tagBadgePopularPrimaryColor || '#b400fa'}
                                    popularSecondaryColor={globalSettings.tagBadgePopularSecondaryColor || '#ff0000'}
                                    onPopularPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgePopularPrimaryColor', color)}
                                    onPopularSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgePopularSecondaryColor', color)}

                                    // New badge colors
                                    newPrimaryColor={globalSettings.tagBadgeNewPrimaryColor || '#ff0000'}
                                    newSecondaryColor={globalSettings.tagBadgeNewSecondaryColor || '#3f92ff'}
                                    onNewPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeNewPrimaryColor', color)}
                                    onNewSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeNewSecondaryColor', color)}

                                    // Deal badge colors
                                    dealPrimaryColor={globalSettings.tagBadgeDealPrimaryColor || '#4fc060'}
                                    dealSecondaryColor={globalSettings.tagBadgeDealSecondaryColor || '#ff0000'}
                                    onDealPrimaryColorChange={(color) => onGlobalSettingChange('tagBadgeDealPrimaryColor', color)}
                                    onDealSecondaryColorChange={(color) => onGlobalSettingChange('tagBadgeDealSecondaryColor', color)}

                                    primaryColor={globalSettings.primaryColor}
                                    secondaryColor={globalSettings.secondaryColor}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </CardContent>
                      </Card>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-md">
        <div className="flex items-start gap-2">
          <div className="text-blue-500 mt-0.5">💡</div>
          <div>
            <div className="font-medium text-sm text-blue-700">
              Enhanced Selection Tips
            </div>
            <ul className="text-xs text-blue-600 mt-1 space-y-1">
              <li>• Some categories support multiple selections (marked with "Multi-Select")</li>
              <li>• Stackable components can be safely combined together</li>
              <li>• Conflicting components will be highlighted and disabled</li>
              <li>• Global settings (colors, animation speed) apply to all selected components</li>
              <li>• Mobile-responsive styles are included automatically</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentSelector;